import { Skeleton } from "@/components/_components/skeleton";
import { Card, CardContent } from "@/components/_components/card";
import { Button } from "@/components/_components/button";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageHeader } from "@/contexts/page-header-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from "lucide-react";
import { getCampaignDetailsRoute } from "@/constants/routes";
import { useNavigate } from "react-router-dom";
import {
  AGENT_TYPE,
  PAGINATION,
} from "@/constants/constants";
import { usePaginatedQuery } from "@/hooks/usePaginatedQuery";
import * as api from "@/lib/api/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/_components/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/_components/badge";
import { PageAccessGuard } from "@/components/auth/PageAccessGuard";
import {
  Plus,
  AlertCircle,
  Loader2,
  BarChart3,
  Users,
  Search,
  RotateCcw
} from "lucide-react";
import { ErrorDisplay } from "@/components/_components/error-display";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuthorities } from "@/hooks/useAuthorities";

interface CampaignFilter {
  campaignTypeQuery?: string;
  agentType?: string;
}

interface CampaignTableData {
  id: number;
  serialNumber: number;
  agentType: string;
  campaignName: string;
  campaignCode: string;
}

// Campaign Table Component
function CampaignTable({ 
  data, 
  navigate,
  currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  isLoading
}: { 
  data: CampaignTableData[]; 
  navigate: (path: string) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  isLoading?: boolean;
}) {
  const COLUMN_COUNT = 5;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  return (
    <>
      <div className="w-full">
        <div className="overflow-hidden rounded-lg border border-border/50">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/60">
              <TableRow>
                <TableHead className="font-semibold">Sl No</TableHead>
                <TableHead className="font-semibold">Agent Type</TableHead>
                <TableHead className="font-semibold">Campaign Name</TableHead>
                <TableHead className="font-semibold">Campaign Code</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="h-24 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <AlertCircle className="h-8 w-8 text-muted-foreground/50" />
                      <span>No results found</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row) => (
                  <TableRow
                    key={row.id}
                    className="transition-colors hover:bg-muted/50 cursor-pointer"
                    onClick={() => row.id && navigate(getCampaignDetailsRoute(row.id))}
                  >
                    <TableCell className="font-medium">{row.serialNumber}</TableCell>
                    <TableCell>{row.agentType}</TableCell>
                    <TableCell className="font-medium">{row.campaignName}</TableCell>
                    <TableCell className="font-medium">{row.campaignCode}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 transition-colors hover:bg-primary/10 hover:text-primary"
                        onClick={(e) => { e.stopPropagation(); row.id && navigate(getCampaignDetailsRoute(row.id)); }}
                        title="View Campaign Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 mt-4">
        <div className="hidden flex-1 text-sm text-muted-foreground lg:flex">
          {totalCount} {totalCount === 1 ? 'record' : 'records'}
        </div>
        <div className="flex w-full items-center gap-8 lg:w-fit">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows per page</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(value) => onPageSizeChange(Number(value))}
              disabled={isLoading}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGINATION.PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                onClick={() => onPageChange(1)} disabled={currentPage === 1 || isLoading} title="First page">
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} title="Previous page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages || isLoading} title="Next page">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages || isLoading} title="Last page">
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const CampaignManagementPage: React.FC = () => {
  return (
    <PageAccessGuard authority="ADMIN_CAMPAIGN_MGMT_VIEW">
      <CampaignManagementContent />
    </PageAccessGuard>
  );
};

const CampaignManagementContent: React.FC = () => {
  const navigate = useNavigate();
  const { setHeaderData } = usePageHeader();
  const { handleError } = useErrorHandler();
  const { hasAuthority } = useAuthorities();
  const [formData, setFormData] = useState({
    agentType: "",
    campaignType: "",
  });
  // Default page size should match one of the Select options (10, 20, 50, 100)
  const [pageSize, setPageSize] = useState(20);
  const [isSearching, setIsSearching] = useState(false);
  const currentFilterRef = useRef<CampaignFilter>({});

  const canUpdate = hasAuthority('ADMIN_CAMPAIGN_MGMT_UPDATE');

  // Use paginated query hook - key it by pageSize to recreate when it changes
  const paginatedQuery = usePaginatedQuery<CampaignFilter, any[]>(
    (filter, options) => {
      return api.getAllCampaigns(filter, options);
    },
    {
      pageSize: pageSize,
      onError: (error) => {
        handleError(error);
      }
    }
  );

  const {
    data: campaignData,
    isPending: isLoading,
    error,
    executeQuery,
    currentPage,
    totalCount: total,
  } = paginatedQuery;

  // Clear isSearching once the query finishes
  useEffect(() => {
    if (!isLoading) setIsSearching(false);
  }, [isLoading]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = useCallback(() => {
    const filter: CampaignFilter = {
      ...(formData.campaignType && { campaignTypeQuery: formData.campaignType }),
      ...(formData.agentType && formData.agentType !== "all" && { agentType: formData.agentType }),
    };
    currentFilterRef.current = filter;
    setIsSearching(true);
    executeQuery(filter, 1);
  }, [formData, executeQuery]);

  const handleReset = useCallback(() => {
    setFormData({
      agentType: "",
      campaignType: "",
    });
    currentFilterRef.current = {};
    setIsSearching(true);
    executeQuery({}, 1);
  }, [executeQuery]);

  // Use a ref to track if we've already loaded initial data
  const initialLoadRef = useRef(false);

  // Simple initial load on mount
  useEffect(() => {
    const performInitialLoad = () => {
      if (initialLoadRef.current) return;
      initialLoadRef.current = true;
      
      const filter = {};
      currentFilterRef.current = filter;
      executeQuery(filter, 1);
    };

    // Small delay to ensure everything is ready
    const timer = setTimeout(performInitialLoad, 100);
    return () => clearTimeout(timer);
  }, []); // Empty dependency array

  // Re-query when pageSize changes to ensure correct pagination
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // (handled inline in onPageSizeChange)


  // Set page header data - only stats and actions, no title
  useEffect(() => {
    setHeaderData({
      stats: campaignData ? {
        count: total,
        label: 'campaign',
        icon: <BarChart3 className="h-4 w-4" />
      } : undefined,
      actions: canUpdate ? (
        <Link to="/campaign-management/create">
          <Button className="h-9 px-2 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-semibold text-sm rounded-md">
            <Plus className="h-4 w-3 " />
            New Campaign
          </Button>
        </Link>
      ) : undefined
    });

    // Cleanup: clear header when component unmounts
    return () => {
      setHeaderData(null);
    };
  }, [campaignData, total, canUpdate, setHeaderData]);

  if (isLoading && !campaignData) {
    return (
      <div className="bg-background">
        {/* Header Skeleton */}
        <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                </div>
                <div>
                  <Skeleton className="h-6 w-48 mb-1" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
              <Skeleton className="h-10 w-32" />
            </div>
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="border border-border/50 bg-card backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-10 w-32" />
              </div>
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background">
        <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
          <ErrorDisplay 
            error={error} 
            onRetry={() => {
              if (currentFilterRef.current) {
                executeQuery(currentFilterRef.current, currentPage);
              }
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background">
      {/* Main Content */}
      <div className="w-full mx-auto px-2 sm:px-2 lg:px-2 py-4">
        <div className="space-y-4">
          {/* Search & Filter Section - Modern Clean Design */}
          <div className="bg-card border border-border/50 rounded-lg">
            {/* Compact Header */}
            <div className="px-4 pt-2 pb-1 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 bg-primary/10 rounded-md">
                  <Search className="h-4 w-4 text-primary" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-foreground leading-tight">
                    Search & Filter
                  </h3>
                  <span className="text-sm text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">
                    Find campaigns by type and agent
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Filter Controls */}
            <div className="px-3 pb-3">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Search Input */}
                <div className="flex-1 min-w-[200px]">
                  <label htmlFor="campaignType" className="sr-only">
                    Search campaigns
                  </label>
                  <Input
                    id="campaignType"
                    type="text"
                    placeholder="Search campaigns..."
                    value={formData.campaignType}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange("campaignType", e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    className="h-9 bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  />
                </div>

                {/* Agent Type Filter */}
                <div className="min-w-[150px]">
                  <label htmlFor="agentType" className="sr-only">
                    Agent type
                  </label>
                  <Select
                    value={formData.agentType}
                    onValueChange={(value) => handleInputChange("agentType", value)}
                  >
                    <SelectTrigger className="h-9 bg-background border border-border hover:border-primary transition-colors focus:ring-2 focus:ring-primary/20">
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agent Types</SelectItem>
                      {AGENT_TYPE.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button 
                    onClick={handleSearch} 
                    disabled={isSearching && isLoading} 
                    className="h-9 px-4 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 font-semibold text-sm rounded-md"
                  >
                    {isSearching && isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="h-4 w-4 mr-1.5" />
                        Search
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleReset} 
                    disabled={isSearching && isLoading}
                    className="h-9 px-3 bg-primary hover:bg-primary/90 text-white shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm rounded-md"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Reset
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {campaignData && (
            <>
              {Array.isArray(campaignData) && campaignData.length === 0 ? (
                <Card className="border border-border/50 bg-card backdrop-blur-sm">
                  <CardContent className="p-12 text-center">
                    <div className="max-w-md mx-auto">
                      <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">No Campaigns Found</h3>
                      <p className="text-muted-foreground mb-4">
                        Get started by creating your first campaign or adjust your search criteria.
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button
                          variant="outline"
                          onClick={handleReset}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50 shadow-sm"
                        >
                          Clear Filters
                        </Button>
                        {canUpdate && (
                          <Link to="/campaign-management/create">
                            <Button className="bg-blue-600 hover:bg-blue-700 text-white shadow-md">
                              <Plus className="h-4 w-4 mr-2" />
                              Create Campaign
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card className="border border-border/50 bg-card backdrop-blur-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg transition-colors hover:bg-primary/20">
                          <Users className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <h2 className="text-xl font-bold text-foreground">
                            Campaign Results
                          </h2>
                          <Badge variant="secondary" className="text-xs font-medium">
                            {total} {total === 1 ? 'record' : 'records'}
                          </Badge>
                          <span className="text-muted-foreground/60">•</span>
                          <p className="text-sm text-muted-foreground">
                            Manage your engagement campaigns and track performance
                          </p>
                        </div>
                      </div>

                      {(() => {
                        // Transform campaign data to table format
                        const tableData: CampaignTableData[] = campaignData?.map((row, index) => ({
                          id: row.id || index + 1,
                          serialNumber: (currentPage - 1) * pageSize + index + 1,
                          agentType: row.agentType || "N/A",
                          campaignName: row.name || "Unnamed Campaign",
                          campaignCode: row.code || "",
                        })) || [];

                        return (
                          <CampaignTable 
                            data={tableData} 
                            navigate={navigate}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalCount={total}
                            onPageChange={(page) => {
                              executeQuery(currentFilterRef.current, page);
                            }}
                            onPageSizeChange={(newSize) => {
                              setPageSize(newSize);
                              executeQuery(currentFilterRef.current, 1, newSize);
                            }}
                            isLoading={isSearching && isLoading}
                          />
                        );
                      })()}
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignManagementPage;